import {
  Badge,
  Button,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Switch
} from "@veritrellis/ui";
import { ChevronDown, ChevronUp, Trash2 } from "lucide-react";
import { ApprovalBlock } from "./ApprovalBlock.js";
import { ConditionRow } from "./ConditionRow.js";
import { emptyCondition } from "./builderTypes.js";
import {
  ruleDecisionBadgeVariant,
  ruleDecisionLabel,
  ruleTitleFromConditions
} from "../../lib/policyPresentation.js";
import type { PlanFeatures } from "../../hooks/useWorkspaceBilling.js";
import type { BuilderRule } from "./builderTypes.js";
import type { PolicyModifications } from "@veritrellis/shared";

type PolicyRuleCardProps = {
  rule: BuilderRule;
  index: number;
  total: number;
  workspaceId: string;
  environment: "sandbox" | "production";
  planFeatures: PlanFeatures;
  billingLoaded?: boolean;
  onChange: (updated: BuilderRule) => void;
  onRemove: () => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  disabled?: boolean;
  matched?: boolean;
};

const decisionOptions = [
  { value: "allow", label: "Allow" },
  { value: "deny", label: "Deny" },
  { value: "require_approval", label: "Require approval" },
  { value: "require_modified_parameters", label: "Modify parameters" }
] as const;

export const PolicyRuleCard = ({
  rule,
  index,
  total,
  workspaceId,
  environment,
  planFeatures,
  billingLoaded = true,
  onChange,
  onRemove,
  onMoveUp,
  onMoveDown,
  disabled = false,
  matched = false
}: PolicyRuleCardProps) => {
  const addCondition = () => {
    onChange({ ...rule, conditions: [...rule.conditions, emptyCondition(crypto.randomUUID())] });
  };

  const mods = rule.modifications ?? {};

  return (
    <article
      className={`admin-policy-rule${matched ? " admin-policy-rule-matched" : ""}`}
      aria-label={`Rule ${String(index + 1)}`}
    >
      <div className="admin-policy-rule-header">
        <span className="admin-policy-rule-index" aria-hidden>
          {index + 1}
        </span>
        <div className="admin-policy-rule-header-text">
          <p className="admin-policy-rule-title">{ruleTitleFromConditions(rule)}</p>
          <Badge variant={ruleDecisionBadgeVariant(rule.decision)} className="normal-case">
            {ruleDecisionLabel[rule.decision]}
          </Badge>
        </div>
        <div className="admin-policy-rule-actions">
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled || index === 0}
            onClick={onMoveUp}
            aria-label="Move rule up"
          >
            <ChevronUp style={{ width: 16, height: 16 }} />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled || index >= total - 1}
            onClick={onMoveDown}
            aria-label="Move rule down"
          >
            <ChevronDown style={{ width: 16, height: 16 }} />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            disabled={disabled}
            onClick={onRemove}
            aria-label="Remove rule"
          >
            <Trash2 style={{ width: 16, height: 16 }} />
          </Button>
        </div>
      </div>

      <div className="admin-policy-rule-body">
        <div className="admin-policy-rule-section">
          <p className="admin-policy-rule-section-label">When (all match)</p>
          <div className="admin-policy-conditions">
            {rule.conditions.length === 0 ? (
              <p className="admin-condition-empty-hint">
                No filters yet — add an attribute to match requests.
              </p>
            ) : null}
            {rule.conditions.map((cond, ci) => (
              <div key={cond.id} className="admin-condition-stack-item">
                {ci > 0 ? (
                  <div className="admin-condition-join" aria-hidden>
                    <span className="admin-condition-join-label">AND</span>
                  </div>
                ) : null}
                <ConditionRow
                  condition={cond}
                  onChange={(updated) => {
                    const conditions = rule.conditions.map((c, i) => (i === ci ? updated : c));
                    onChange({ ...rule, conditions });
                  }}
                  onRemove={() => {
                    onChange({ ...rule, conditions: rule.conditions.filter((_, i) => i !== ci) });
                  }}
                  disabled={disabled}
                />
              </div>
            ))}
          </div>
          <button
            type="button"
            className="admin-condition-add"
            disabled={disabled}
            onClick={addCondition}
          >
            + Add filter
          </button>
        </div>

        <div className="admin-policy-rule-section">
          <p className="admin-policy-rule-section-label">Then</p>
          <div className="admin-form-field admin-form-field-full">
            <p className="admin-form-field-hint">Outcome when all filters above match</p>
            <div className="admin-form-field-control admin-policy-then-control">
              <Select
                value={rule.decision}
                onValueChange={(v) => {
                  onChange({ ...rule, decision: v as BuilderRule["decision"] });
                }}
                disabled={disabled}
              >
                <SelectTrigger style={{ height: 36, width: "100%" }}>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {decisionOptions.map((d) => (
                    <SelectItem key={d.value} value={d.value}>
                      {d.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
        </div>

        {rule.decision === "require_approval" ? (
          <ApprovalBlock
            workspaceId={workspaceId}
            environment={environment}
            approverGroupId={rule.approverGroupId}
            permitTtlOverride={rule.permitTtlSeconds}
            approvalGroupsEnabled={planFeatures.approvalGroups}
            billingLoaded={billingLoaded}
            onGroupChange={(id) => onChange({ ...rule, approverGroupId: id })}
            onTtlChange={(seconds) => onChange({ ...rule, permitTtlSeconds: seconds })}
            disabled={disabled}
          />
        ) : null}

        {rule.decision === "require_modified_parameters" ? (
          <div className="admin-policy-modifications">
            <p
              className="admin-approval-settings-title"
              style={{ color: "var(--muted-foreground)" }}
            >
              Parameter modifications
            </p>
            <div className="admin-form-field">
              <label className="admin-form-field-label" htmlFor={`cap-amount-${rule.id}`}>
                Cap amount
              </label>
              <p className="admin-form-field-hint">Maximum amount allowed after modification</p>
              <div className="admin-form-field-control">
                <Input
                  id={`cap-amount-${rule.id}`}
                  type="number"
                  style={{ height: 36, width: "100%" }}
                  disabled={disabled}
                  placeholder="e.g. 500"
                  value={mods.cap_amount ?? ""}
                  onChange={(e) => {
                    const raw = e.currentTarget.value;
                    const cap = raw.length === 0 ? undefined : Number(raw);
                    const next: PolicyModifications = { ...mods };
                    if (cap === undefined || Number.isNaN(cap)) {
                      delete next.cap_amount;
                    } else {
                      next.cap_amount = cap;
                    }
                    onChange({ ...rule, modifications: next });
                  }}
                />
              </div>
            </div>
            <div className="admin-policy-mod-switch-row">
              <Switch
                id={`require-reason-${rule.id}`}
                disabled={disabled}
                checked={(mods.require_fields ?? []).includes("reason")}
                onCheckedChange={(checked) => {
                  const fields = mods.require_fields ?? [];
                  const nextFields = checked
                    ? [...fields.filter((f) => f !== "reason"), "reason"]
                    : fields.filter((f) => f !== "reason");
                  onChange({
                    ...rule,
                    modifications: {
                      ...mods,
                      require_fields: nextFields.length > 0 ? nextFields : undefined
                    }
                  });
                }}
              />
              <label htmlFor={`require-reason-${rule.id}`}>Require reason field on resubmit</label>
            </div>
          </div>
        ) : null}
      </div>
    </article>
  );
};
